import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag, ChevronLeft } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const BOX_OPTIONS = [
  { id: "cake-bundle-6", count: 6, price: 1650, label: "Box of 6" },
  { id: "cake-bundle-12", count: 12, price: 3300, label: "Box of 12" },
];

export default function BundleBuilder() {
  const [cakes, setCakes] = useState([]);
  const [bundleProducts, setBundleProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [boxId, setBoxId] = useState(BOX_OPTIONS[0].id);
  const [picks, setPicks] = useState({}); // { cakeName: count }
  const { addBundleToCart } = useApp();

  const box = BOX_OPTIONS.find((b) => b.id === boxId);

  useEffect(() => {
    Promise.all([
      api.get("/products?category=cakes"),
      api.get("/products?category=bundles"),
    ])
      .then(([cakesRes, bundlesRes]) => {
        setCakes(cakesRes.data || []);
        const bmap = {};
        (bundlesRes.data || []).forEach((b) => (bmap[b.id] = b));
        setBundleProducts(bmap);
      })
      .finally(() => setLoading(false));
  }, []);

  // Reset picks when box size changes
  useEffect(() => {
    setPicks({});
  }, [boxId]);

  const totalPicked = useMemo(
    () => Object.values(picks).reduce((s, n) => s + n, 0),
    [picks]
  );
  const remaining = box.count - totalPicked;
  const isComplete = totalPicked === box.count;

  const inc = (name) => {
    if (totalPicked >= box.count) return;
    setPicks((p) => ({ ...p, [name]: (p[name] || 0) + 1 }));
  };
  const dec = (name) => {
    setPicks((p) => {
      const next = { ...p, [name]: Math.max(0, (p[name] || 0) - 1) };
      if (next[name] === 0) delete next[name];
      return next;
    });
  };

  const handleAdd = () => {
    if (!isComplete) return;
    const product = bundleProducts[box.id];
    if (!product) {
      toast.error("Bundle product not available. Please refresh and retry.");
      return;
    }
    // Flatten picks to array (each pastry name repeated per quantity picked)
    const selection = [];
    Object.entries(picks).forEach(([name, qty]) => {
      for (let i = 0; i < qty; i++) selection.push(name);
    });
    addBundleToCart(product, selection);
    toast.success(`${box.label} added to your basket.`);
    setPicks({});
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="bundle-builder">
      <SEOHead
        title="Build Your Cake Bundle · Mix Any 6 or 12 Pastries | Cocoamaya Patisserie"
        description="Curate a custom pastry gift box — mix any 6 (₹1650) or 12 (₹3300) chef-portioned slices from our French patisserie in Mumbai. Same-day delivery available."
        keywords="cake bundle mumbai, custom pastry box mumbai, pastry gift box, mixed cake box, cocoamaya bundle"
        url="https://cocoamayapatisserie.com/bundle"
      />

      <Link to="/shop?category=cakes" className="eyebrow inline-flex items-center gap-2 mb-8" data-testid="bundle-back">
        <ChevronLeft size={14} strokeWidth={1.5} /> Back to cakes
      </Link>

      <header className="mb-10 md:mb-14 max-w-3xl">
        <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Build Your Own</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none italic">
          A gift box, exactly your way.
        </h1>
        <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
          Pick your favourite pastries — one slice at a time — into a box of six or twelve.
          Repeats welcome. Each pastry is hand-crafted the morning of delivery.
        </p>
      </header>

      {/* Box size toggle */}
      <div className="flex flex-wrap gap-3 mb-10" data-testid="box-toggle">
        {BOX_OPTIONS.map((b) => (
          <button
            key={b.id}
            onClick={() => setBoxId(b.id)}
            className="px-6 py-3 border rounded-full text-sm transition-colors"
            style={{
              borderColor: boxId === b.id ? "var(--accent)" : "var(--line)",
              background: boxId === b.id ? "var(--accent)" : "transparent",
              color: boxId === b.id ? "#FAF3EB" : "var(--ink)",
            }}
            data-testid={`box-option-${b.count}`}
          >
            <span className="font-medium">{b.label}</span>
            <span className="ml-2" style={{ opacity: 0.85 }}>₹{b.price}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left: pastry picker grid */}
        <div className="lg:col-span-8">
          {loading ? (
            <p style={{ color: "var(--ink-soft)" }} data-testid="bundle-loading">Loading pastries…</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="pastry-grid">
              {cakes.map((c) => {
                const heroImg = (c.variant_images && c.variant_images.Pastry) || c.image;
                const count = picks[c.name] || 0;
                const canAdd = totalPicked < box.count;
                return (
                  <div
                    key={c.id}
                    className="border rounded-2xl overflow-hidden flex flex-col"
                    style={{ borderColor: count > 0 ? "var(--accent)" : "var(--line)", background: "var(--bg-card)" }}
                    data-testid={`pastry-card-${c.slug}`}
                  >
                    <div className="aspect-square overflow-hidden relative">
                      <img src={heroImg} alt={c.name} className="w-full h-full object-cover" />
                      {count > 0 && (
                        <span
                          className="absolute top-2 right-2 text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center"
                          style={{ background: "var(--accent)", color: "#FAF3EB" }}
                          data-testid={`pastry-count-${c.slug}`}
                        >
                          {count}
                        </span>
                      )}
                    </div>
                    <div className="p-3 flex flex-col gap-2 flex-1">
                      <p className="font-display text-sm leading-tight">{c.name}</p>
                      {count === 0 ? (
                        <button
                          onClick={() => inc(c.name)}
                          disabled={!canAdd}
                          className="mt-auto text-xs eyebrow border rounded-full py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--bg)]"
                          style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
                          data-testid={`pastry-add-${c.slug}`}
                        >
                          + Add
                        </button>
                      ) : (
                        <div className="mt-auto flex items-center justify-between border rounded-full px-2 py-1" style={{ borderColor: "var(--accent)" }}>
                          <button
                            onClick={() => dec(c.name)}
                            className="w-7 h-7 flex items-center justify-center"
                            data-testid={`pastry-dec-${c.slug}`}
                            aria-label={`Remove one ${c.name}`}
                          >
                            <Minus size={13} strokeWidth={1.5} />
                          </button>
                          <span className="text-sm">{count}</span>
                          <button
                            onClick={() => inc(c.name)}
                            disabled={!canAdd}
                            className="w-7 h-7 flex items-center justify-center disabled:opacity-30"
                            data-testid={`pastry-inc-${c.slug}`}
                            aria-label={`Add one more ${c.name}`}
                          >
                            <Plus size={13} strokeWidth={1.5} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: live summary */}
        <aside className="lg:col-span-4">
          <div
            className="lg:sticky lg:top-6 border rounded-2xl p-6"
            style={{ borderColor: "var(--line)", background: "var(--bg-card)" }}
            data-testid="bundle-summary"
          >
            <p className="eyebrow mb-2">Your box</p>
            <h2 className="font-display text-2xl mb-1">{box.label}</h2>
            <p className="text-3xl font-display mb-4">₹{box.price}</p>

            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span style={{ color: "var(--ink-soft)" }}>Picked</span>
                <span data-testid="picked-count">{totalPicked} / {box.count}</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-soft)" }}>
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${Math.min(100, (totalPicked / box.count) * 100)}%`,
                    background: "var(--accent)",
                  }}
                />
              </div>
              {!isComplete && (
                <p className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }} data-testid="bundle-remaining">
                  Add {remaining} more to complete your box.
                </p>
              )}
            </div>

            {totalPicked > 0 && (
              <ul className="mb-5 space-y-1.5 text-sm" data-testid="bundle-selection-list">
                {Object.entries(picks).map(([name, qty]) => (
                  <li key={name} className="flex justify-between">
                    <span style={{ color: "var(--ink-soft)" }}>{name}</span>
                    <span>× {qty}</span>
                  </li>
                ))}
              </ul>
            )}

            <button
              onClick={handleAdd}
              disabled={!isComplete}
              className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bundle-add-to-cart"
            >
              <ShoppingBag size={16} strokeWidth={1.5} />
              {isComplete ? `Add box · ₹${box.price}` : `Pick ${remaining} more`}
            </button>

            <p className="mt-4 text-xs leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              Freshly baked the morning of delivery. Same-day delivery across Mumbai via Borzo courier — flat ₹300, or free with store pickup.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
